import { useState } from 'react';
import { getAmplifyClient } from '../lib/amplifyClient';

export interface ImportDynamicPriceDataInput {
  csvContent?: string;
  jsonContent?: string;
  dataSource?: 'CSV_IMPORT' | 'API_PSE_RCE' | 'MANUAL';
}

export interface ImportDynamicPriceDataStats {
  avgPriceKwh: number;
  minPriceKwh: number;
  maxPriceKwh: number;
  avgPriceMwh: number;
  minPriceMwh: number;
  maxPriceMwh: number;
}

export interface ImportDynamicPriceDataResult {
  success: boolean;
  message?: string;
  importedMonths: string[];
  totalHours: number;
  errors?: string[];
  stats?: ImportDynamicPriceDataStats;
}

export interface UseImportDynamicPriceDataReturn {
  importDynamicPriceData: (input: ImportDynamicPriceDataInput) => Promise<ImportDynamicPriceDataResult>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

export function useImportDynamicPriceData(): UseImportDynamicPriceDataReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const importDynamicPriceData = async (input: ImportDynamicPriceDataInput): Promise<ImportDynamicPriceDataResult> => {
    setLoading(true);
    setError(null);

    try {
      const client = getAmplifyClient();

      const inputJson = JSON.stringify({
        csvContent: input.csvContent,
        jsonContent: input.jsonContent,
        dataSource: input.dataSource || 'CSV_IMPORT',
      });

      const result = await client.mutations.importDynamicPriceData({
        input: inputJson,
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data) {
        throw new Error('No data returned from import');
      }

      // Parse the AWSJSON response (it may be double-stringified by GraphQL)
      let parsedResult = JSON.parse(result.data as string);

      // If still a string after first parse, parse again (double-stringified response)
      if (typeof parsedResult === 'string') {
        parsedResult = JSON.parse(parsedResult);
      }

      const typedResult: ImportDynamicPriceDataResult = parsedResult;

      if (!typedResult.success) {
        throw new Error(typedResult.message || 'Import failed');
      }

      return typedResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setLoading(false);
    setError(null);
  };

  return {
    importDynamicPriceData,
    loading,
    error,
    reset,
  };
}
